import { useInfiniteQuery } from "@tanstack/react-query";

import {
  getGetPaymentsPaymentsGetQueryKey,
  getPaymentsPaymentsGet,
  type GetPaymentsPaymentsGetParams,
  type PaymentStatus,
  type PaymentType,
} from "@/api-v3";

export interface PaymentsFilters {
  status?: PaymentStatus[];
  types?: PaymentType[];
}

function buildFilterParams(
  filters: PaymentsFilters
): Omit<GetPaymentsPaymentsGetParams, "page"> {
  const params: Omit<GetPaymentsPaymentsGetParams, "page"> = {};
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.types && filters.types.length > 0) {
    params["filter[type]"] = filters.types;
  }
  return params;
}

export function useGetInfinitePayments(filters: PaymentsFilters = {}) {
  const filterParams = buildFilterParams(filters);

  const {
    data: payments,
    fetchNextPage: getNextPageOfPayments,
    isFetching: isPaymentsLoading,
    isError: hasPaymentsErrored,
    hasNextPage: hasPaymentNextPage,
  } = useInfiniteQuery({
    queryKey: [getGetPaymentsPaymentsGetQueryKey(filterParams)],
    queryFn: async ({ pageParam }) =>
      getPaymentsPaymentsGet({ page: pageParam, ...filterParams }),
    initialPageParam: 1,
    getNextPageParam: (data) => {
      const { pagination } = data;
      // Get next page number if there is another page to get
      if (pagination?.has_next) {
        const pageNumber = pagination?.page ?? 0;
        return pageNumber + 1;
      }
      // Returning undefined as lets react query know no more pages
      return undefined;
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.data);
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  return {
    payments,
    getNextPageOfPayments,
    isPaymentsLoading,
    hasPaymentsErrored,
    hasPaymentNextPage,
  };
}
